import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getMarketplaceIntegrationsApiV1OrganizationsOrgIdMarketplaceIntegrationsGet } from '@/common/api/client/sdk.gen'
import type { MarketplaceIntegrationResponse } from '@/common/api/client/types.gen'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'
import {
    closeIntegrationModal,
    closeMarketplaceOauthModal,
    getMarketplaceOauthUrl,
    INTEGRATIONS_PAGE_PATH,
    MARKETPLACE_OAUTH_MESSAGE_SUCCESS,
    MARKETPLACE_OAUTH_POPUP_NAME,
    openMarketplaceOauthModal,
    parseMarketplaceOauthMessage,
} from '../lib/marketplaceOauth'

const POPUP_WIDTH = 1000
const POPUP_HEIGHT = 700
const POPUP_POLL_INTERVAL_MS = 500
const SUCCESS_MODAL_CLOSE_DELAY_MS = 1500

export type MarketplaceOauthModalPhase = 'idle' | 'loading' | 'success' | 'error'

type UseMarketplaceOauthIntegrationOptions = {
    orgId?: string
    integrationId?: string
    modalId: string
    enabled?: boolean
    isEditMode?: boolean
    onSuccess?: () => void
}

type OauthIntegrationsSnapshot = {
    updatedAt?: string
}

export const useMarketplaceOauthIntegration = ({
    orgId,
    integrationId,
    modalId,
    enabled = true,
    isEditMode = false,
    onSuccess,
}: UseMarketplaceOauthIntegrationOptions) => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const queryClient = useQueryClient()
    const toast = useOptionalToast()
    const [oauthModalPhase, setOauthModalPhase] = useState<MarketplaceOauthModalPhase>('idle')
    const [oauthErrorMessage, setOauthErrorMessage] = useState<string | null>(null)
    const popupRef = useRef<Window | null>(null)
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const successCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const completedRef = useRef(false)
    const isCheckingPopupCloseRef = useRef(false)
    const oauthSnapshotRef = useRef<OauthIntegrationsSnapshot | null>(null)

    const orgIntegrationsQueryKey = useMemo(() => ['org_integrations', orgId], [orgId])

    const getOrgIntegrations = useCallback(async () => {
        if (!orgId) {
            return []
        }

        const response = await getMarketplaceIntegrationsApiV1OrganizationsOrgIdMarketplaceIntegrationsGet({
            path: { org_id: orgId },
        })

        return response.data ?? []
    }, [orgId])

    const clearPollTimer = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current)
            pollTimerRef.current = null
        }
    }, [])

    const clearSuccessCloseTimer = useCallback(() => {
        if (successCloseTimerRef.current) {
            clearTimeout(successCloseTimerRef.current)
            successCloseTimerRef.current = null
        }
    }, [])

    const resetOauthFlow = useCallback(() => {
        completedRef.current = false
        isCheckingPopupCloseRef.current = false
        oauthSnapshotRef.current = null
        clearPollTimer()
        clearSuccessCloseTimer()
        setOauthModalPhase('idle')
        setOauthErrorMessage(null)
        closeMarketplaceOauthModal()
        popupRef.current = null
    }, [clearPollTimer, clearSuccessCloseTimer])

    const finishOauthSuccess = useCallback(async () => {
        if (orgId) {
            await queryClient.invalidateQueries({ queryKey: orgIntegrationsQueryKey })
            await queryClient.refetchQueries({ queryKey: orgIntegrationsQueryKey, type: 'active' })
        }

        closeMarketplaceOauthModal()
        closeIntegrationModal(modalId)
        onSuccess?.()
        setOauthModalPhase('idle')
        setOauthErrorMessage(null)
        completedRef.current = false
        isCheckingPopupCloseRef.current = false
        oauthSnapshotRef.current = null
        popupRef.current = null
        clearPollTimer()
    }, [clearPollTimer, modalId, onSuccess, orgId, orgIntegrationsQueryKey, queryClient])

    const handleOauthSuccess = useCallback(() => {
        if (completedRef.current) {
            return
        }
        completedRef.current = true
        clearPollTimer()
        popupRef.current = null
        setOauthModalPhase('success')
        setOauthErrorMessage(null)

        successCloseTimerRef.current = setTimeout(() => {
            void finishOauthSuccess()
        }, SUCCESS_MODAL_CLOSE_DELAY_MS)
    }, [clearPollTimer, finishOauthSuccess])

    const showOauthError = useCallback(
        (errorMessage?: string) => {
            if (completedRef.current && oauthModalPhase === 'error') {
                return
            }
            completedRef.current = true
            isCheckingPopupCloseRef.current = false
            clearPollTimer()
            popupRef.current = null
            setOauthErrorMessage(errorMessage || 'Не удалось подключить интеграцию через Ozon Seller Id')
            setOauthModalPhase('error')
            openMarketplaceOauthModal()
        },
        [clearPollTimer, oauthModalPhase]
    )

    useEffect(() => {
        if (searchParams.get('marketplace_oauth_error') !== '1') {
            return
        }

        toast?.showToast({
            type: 'error',
            message: 'Не удалось подключить интеграцию через Ozon Seller Id',
        })

        const nextParams = new URLSearchParams(searchParams)
        nextParams.delete('marketplace_oauth_error')
        const nextSearch = nextParams.toString()
        navigate(
            {
                pathname: INTEGRATIONS_PAGE_PATH,
                search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true }
        )
    }, [navigate, searchParams, toast])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const message = parseMarketplaceOauthMessage(event)
            if (!message) {
                return
            }

            if (message.type === MARKETPLACE_OAUTH_MESSAGE_SUCCESS) {
                handleOauthSuccess()
                return
            }

            showOauthError(message.error)
        }

        window.addEventListener('message', handleMessage)
        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [handleOauthSuccess, showOauthError])

    useEffect(() => {
        return () => {
            clearPollTimer()
            clearSuccessCloseTimer()
            popupRef.current?.close()
        }
    }, [clearPollTimer, clearSuccessCloseTimer])

    const hasOauthCompleted = useCallback(
        (integrations: MarketplaceIntegrationResponse[]) => {
            if (isEditMode) {
                if (!integrationId) {
                    return false
                }

                const currentIntegration = integrations.find(item => item.id === integrationId)
                return Boolean(
                    currentIntegration &&
                        oauthSnapshotRef.current?.updatedAt &&
                        currentIntegration.updated_at !== oauthSnapshotRef.current.updatedAt
                )
            }

            return integrations.some(item => item.marketplace === 'ozon')
        },
        [integrationId, isEditMode]
    )

    const checkOauthCompletionAfterPopupClose = useCallback(async () => {
        if (!orgId || completedRef.current || isCheckingPopupCloseRef.current) {
            return
        }

        isCheckingPopupCloseRef.current = true

        try {
            const integrations = await queryClient.fetchQuery({
                queryKey: orgIntegrationsQueryKey,
                queryFn: getOrgIntegrations,
            })

            if (completedRef.current) {
                return
            }

            if (hasOauthCompleted(integrations)) {
                handleOauthSuccess()
                return
            }

            resetOauthFlow()
        } catch {
            resetOauthFlow()
        } finally {
            isCheckingPopupCloseRef.current = false
        }
    }, [
        getOrgIntegrations,
        handleOauthSuccess,
        hasOauthCompleted,
        orgId,
        orgIntegrationsQueryKey,
        queryClient,
        resetOauthFlow,
    ])

    const handleMarketplaceOauthClick = useCallback(() => {
        if (!enabled || oauthModalPhase === 'loading' || !orgId) {
            return
        }

        if (isEditMode && !integrationId) {
            return
        }

        completedRef.current = false
        isCheckingPopupCloseRef.current = false
        setOauthErrorMessage(null)
        setOauthModalPhase('loading')
        openMarketplaceOauthModal()

        const cachedIntegrations = (
            queryClient.getQueryData<MarketplaceIntegrationResponse[]>(orgIntegrationsQueryKey) ?? []
        ).filter(item => item.marketplace === 'ozon')

        const currentIntegration = cachedIntegrations.find(item => item.id === integrationId)
        oauthSnapshotRef.current = {
            updatedAt: currentIntegration?.updated_at,
        }

        const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2
        const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2
        const features = `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},resizable=yes,scrollbars=yes`

        const popup = window.open(
            getMarketplaceOauthUrl(orgId, 'ozon', integrationId),
            MARKETPLACE_OAUTH_POPUP_NAME,
            features
        )

        if (!popup) {
            showOauthError('Разрешите всплывающие окна для подключения через Ozon')
            return
        }

        popupRef.current = popup
        clearPollTimer()

        pollTimerRef.current = setInterval(() => {
            if (!popupRef.current || popupRef.current.closed) {
                clearPollTimer()
                popupRef.current = null

                if (!completedRef.current) {
                    void checkOauthCompletionAfterPopupClose()
                }
            }
        }, POPUP_POLL_INTERVAL_MS)
    }, [
        checkOauthCompletionAfterPopupClose,
        clearPollTimer,
        enabled,
        integrationId,
        isEditMode,
        oauthModalPhase,
        orgId,
        orgIntegrationsQueryKey,
        queryClient,
        showOauthError,
    ])

    const handleCancelMarketplaceOauth = useCallback(() => {
        popupRef.current?.close()
        resetOauthFlow()
    }, [resetOauthFlow])

    const handleCloseOauthResultModal = useCallback(() => {
        if (oauthModalPhase === 'success') {
            clearSuccessCloseTimer()
            void finishOauthSuccess()
            return
        }
        resetOauthFlow()
    }, [clearSuccessCloseTimer, finishOauthSuccess, oauthModalPhase, resetOauthFlow])

    const successMessage = isEditMode ? 'Интеграция обновлена' : 'Интеграция создана'

    return {
        oauthModalPhase,
        oauthErrorMessage,
        isOauthLoading: oauthModalPhase === 'loading',
        successMessage,
        handleMarketplaceOauthClick,
        handleCancelMarketplaceOauth,
        handleCloseOauthResultModal,
    }
}
